<template>
  <div class="view-realize-modal">
    <q-linear-progress v-if="loading" indeterminate />
    
    <div v-else>
      <!-- Информация о реализации -->
      <div class="realize-info">
        <div class="info-row">
          <span class="label">Номер:</span>
          <strong>{{ realize?.realize_num }}</strong>
        </div>
        <div class="info-row">
          <span class="label">Дата:</span>
          <span>{{ realize?.realize_date }}</span>
        </div>
        <div class="info-row">
          <span class="label">Сумма:</span>
          <strong class="amount">{{ formatAmount(realize?.realize_sum || totalSum) }}</strong>
        </div>
        <div class="info-row" v-if="realize?.provider_name">
          <span class="label">Поставщик:</span>
          <span>{{ realize?.provider_name }}</span>
        </div>
        <div class="info-row" v-if="realize?.contractor_name">
          <span class="label">Подрядчик:</span>
          <span>{{ realize?.contractor_name }}</span>
        </div>
      </div>

      <!-- Блок подписей -->
      <div v-if="realize?.sign_info" class="sign-info-section">
        <div class="sign-header">
          <span class="sign-title">Подписание</span>
          <q-badge :color="getSignStatusColor(realize.sign_info)" :label="getSignStatusLabel(realize.sign_info)" />
          <span v-if="realize.sign_info.sign_total" class="sign-progress">
            {{ realize.sign_info.sign_signed || 0 }} / {{ realize.sign_info.sign_total }}
          </span>
        </div>
        <q-linear-progress
          v-if="realize.sign_info.progress_percent != null"
          :value="realize.sign_info.progress_percent / 100"
          :color="getSignStatusColor(realize.sign_info)"
          class="sign-progress-bar"
        />
        <div v-if="realize.sign_info.signatures?.length" class="signatures-list">
          <div 
            v-for="(sig, idx) in realize.sign_info.signatures" 
            :key="idx"
            class="signature-item"
          >
            <q-icon 
              :name="sig.status === 'SIGNED' ? 'check_circle' : 'radio_button_unchecked'" 
              :color="sig.status === 'SIGNED' ? 'green' : 'grey'" 
              size="18px"
            />
            <div class="signature-info">
              <span class="signature-desc">{{ sig.description }}</span>
              <span v-if="sig.signed_by?.dn" class="signature-signer">{{ sig.signed_by.dn }}</span>
              <span v-if="sig.signed_at" class="signature-date">{{ formatDateTime(sig.signed_at) }}</span>
            </div>
            <q-badge 
              :color="getSignatureStatusColor(sig.status)" 
              :label="getSignatureStatusLabel(sig.status)" 
              dense
            />
          </div>
        </div>
      </div>

      <!-- Позиции реализации -->
      <div class="items-section">
        <h4>Позиции реализации</h4>
        
        <q-table
          v-if="items.length"
          :rows="items"
          :columns="columns"
          row-key="realize_tem_id"
          flat
          dense
          :pagination="{ rowsPerPage: 0 }"
          hide-pagination
          class="items-table"
        >
          <template v-slot:body-cell-material_name="props">
            <q-td :props="props">
              <div class="material-info" :title="props.row.material_name">
                {{ props.row.material_name }}
              </div>
            </q-td>
          </template>
          <template v-slot:body-cell-material_cnt="props">
            <q-td :props="props" class="text-center">
              <span>{{ props.row.material_cnt }}</span>
              <span class="unit-name">{{ props.row.sell_unit_name }}</span>
            </q-td>
          </template>
          <template v-slot:body-cell-material_price="props">
            <q-td :props="props" class="text-right">
              {{ formatAmount(props.row.material_price) }}
            </q-td>
          </template>
          <template v-slot:body-cell-amount="props">
            <q-td :props="props" class="text-right">
              <strong>{{ formatAmount(props.row.amount) }}</strong>
            </q-td>
          </template>
        </q-table>

        <div v-else class="no-items">
          Нет позиций
        </div>
      </div>

      <!-- Итого -->
      <div class="totals-section">
        <div class="total-row">
          <span>Всего позиций:</span>
          <strong>{{ items.length }}</strong>
        </div>
        <div class="total-row">
          <span>Общая сумма:</span>
          <strong class="total-sum">{{ formatAmount(totalSum) }}</strong>
        </div>
      </div>
    </div>

    <q-card-actions align="right" class="q-mt-md">
      <q-btn
        color="primary"
        icon="download"
        label="Скачать реализацию"
        :loading="downloading"
        :disable="loading || downloading"
        @click="downloadRealizePdf"
      />
      <q-btn flat label="Закрыть" color="grey" @click="closeModal()" />
    </q-card-actions>
  </div>
</template>

<script>
import { getRealize, getRealizeItems, downloadRealize } from "@/views/SupplierRequests/services";

export default {
  name: "ViewRealize",
  props: {
    data: {
      type: Object,
      required: true,
    },
    closeModal: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      downloading: false,
      realizeData: null,
      items: [],
      columns: [
        { name: 'material_name', label: 'Материал', field: 'material_name', align: 'left' },
        { name: 'material_cnt', label: 'Кол-во', field: 'material_cnt', align: 'center' },
        { name: 'material_price', label: 'Цена', field: 'material_price', align: 'right' },
        { name: 'amount', label: 'Сумма', field: 'amount', align: 'right' },
      ],
    };
  },
  computed: {
    realize() {
      // Объединяем данные из props с загруженными данными
      // realize_sum приходит из списка, но не из get запроса
      const propsData = this.data.realize || {};
      const loadedData = this.realizeData || {};
      return { ...propsData, ...loadedData };
    },
    totalSum() {
      return this.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    },
  },
  methods: {
    getRealizeId() {
      return this.data.realize?.realize_id || this.data.realize?.id;
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('ru-RU');
    },
    formatAmount(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value) + ' ₸';
    },
    getSignStatusLabel(signInfo) {
      if (!signInfo) return 'Нет данных';
      if (signInfo.is_fully_signed) return 'Полностью подписано';
      if (signInfo.is_partially_signed) return 'Частично подписано';
      if (signInfo.sign_signed > 0) return 'В процессе';
      return 'Не подписано';
    },
    getSignStatusColor(signInfo) {
      if (!signInfo) return 'grey';
      if (signInfo.is_fully_signed) return 'green';
      if (signInfo.is_partially_signed) return 'blue';
      if (signInfo.sign_signed > 0) return 'blue';
      return 'grey';
    },
    getSignatureStatusLabel(status) {
      const labels = {
        'SIGNED': 'Подписано',
        'NOT_STARTED': 'Ожидает',
        'IN_PROGRESS': 'В процессе',
        'REJECTED': 'Отклонено',
        'EXPIRED': 'Просрочено',
      };
      return labels[status] || status || '-';
    },
    getSignatureStatusColor(status) {
      const colors = {
        'SIGNED': 'green',
        'NOT_STARTED': 'grey',
        'IN_PROGRESS': 'blue',
        'REJECTED': 'red',
        'EXPIRED': 'orange',
      };
      return colors[status] || 'grey';
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleString('ru-RU', { 
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },
    async loadData() {
      const realizeId = this.getRealizeId();
      if (!realizeId) return;
      
      this.loading = true;
      
      // Загружаем данные реализации и позиции параллельно
      const [realizeRes, itemsRes] = await Promise.all([
        getRealize(realizeId),
        getRealizeItems(realizeId)
      ]);
      
      this.realizeData = realizeRes;
      this.items = itemsRes;
      this.loading = false;
    },
    parseFilenameFromDisposition(disposition) {
      if (!disposition) return null;
      const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
      return match ? match[1] : null;
    },
    async downloadRealizePdf() {
      const realizeId = this.getRealizeId();
      if (!realizeId) return;

      this.downloading = true;
      try {
        const response = await downloadRealize(realizeId);
        const contentType = response?.headers?.['content-type'] || 'application/pdf';
        const disposition = response?.headers?.['content-disposition'];
        const filename = this.parseFilenameFromDisposition(disposition) || `Реализация_${realizeId}.pdf`;

        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$q.notify({
          type: 'negative',
          message: e?.response?.data?.error || 'Ошибка формирования реализации',
        });
      } finally {
        this.downloading = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped lang="scss">
.view-realize-modal {
  min-width: 500px;
  max-width: 700px;
  padding: 20px;
}

.realize-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 16px;
  background: #e8f5e9;
  border-radius: 8px;
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.label {
  color: #6c757d;
  font-size: 13px;
}

.amount {
  color: #2e7d32;
  font-size: 16px;
}

.items-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;

  h4 {
    margin: 0;
    padding: 12px 16px;
    background: #f5f5f5;
    font-size: 14px;
    font-weight: 600;
  }
}

.items-table {
  max-height: 300px;
  overflow-y: auto;
}

.material-info {
  max-width: 300px;
  white-space: normal;
  line-height: 1.3;
}

.unit-name {
  font-size: 11px;
  color: #6c757d;
  margin-left: 4px;
}

.no-items {
  padding: 20px;
  text-align: center;
  color: #9e9e9e;
}

.totals-section {
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #e8f5e9;
  border-radius: 8px;
}

.total-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.total-sum {
  color: #2e7d32;
  font-size: 16px;
}

.sign-info-section {
  margin-top: 16px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.sign-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.sign-title {
  font-weight: 600;
  font-size: 14px;
  color: #424242;
}

.sign-progress {
  font-size: 12px;
  color: #757575;
  margin-left: auto;
}

.sign-progress-bar {
  margin-bottom: 12px;
  border-radius: 4px;
}

.signatures-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.signature-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px;
  background: white;
  border-radius: 6px;
  border: 1px solid #eeeeee;
}

.signature-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.signature-desc {
  font-size: 13px;
  color: #424242;
}

.signature-signer {
  font-size: 12px;
  color: #1976d2;
  font-weight: 500;
}

.signature-date {
  font-size: 11px;
  color: #9e9e9e;
}
</style>

